<template>
  <section v-if="hasData" class="burnup" data-test="burnup-chart">
    <header class="burnup-head">
      <div>
        <h3 class="burnup-title">Burnup</h3>
        <span class="burnup-sub">Daily scope vs delivered, with mid-sprint additions marked.</span>
      </div>
      <div class="legend">
        <span class="leg"><span class="dot scope" />Scope</span>
        <span class="leg"><span class="dot delivered" />Delivered</span>
        <span class="leg"><span class="dot ideal" />Ideal</span>
        <span v-if="events.length" class="leg"><span class="dot event" />Mid-sprint adds</span>
      </div>
    </header>

    <div ref="host" class="burnup-host">
      <svg
        class="burnup-svg"
        :viewBox="`0 0 ${W} ${H}`"
        preserveAspectRatio="none"
        role="img"
        aria-label="Sprint burnup chart"
      >
        <defs>
          <linearGradient id="burnup-scope-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stop-color="var(--blue)" stop-opacity="0.22" />
            <stop offset="100%" stop-color="var(--blue)" stop-opacity="0" />
          </linearGradient>
          <linearGradient id="burnup-deliv-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stop-color="var(--green)" stop-opacity="0.35" />
            <stop offset="100%" stop-color="var(--green)" stop-opacity="0" />
          </linearGradient>
        </defs>

        <!-- Y-axis grid + tick labels -->
        <g class="grid">
          <template v-for="tick in yTicks" :key="`g${tick}`">
            <line
              :x1="margin.left" :x2="margin.left + plotW"
              :y1="yForVal(tick)" :y2="yForVal(tick)"
              class="grid-line"
            />
            <text
              :x="margin.left - 8" :y="yForVal(tick) + 4"
              class="tick" text-anchor="end"
            >{{ tick }}</text>
          </template>
        </g>

        <!-- Mid-sprint additions -->
        <line
          v-for="ev in eventLines"
          :key="`ev${ev.date}`"
          :x1="ev.x" :x2="ev.x"
          :y1="margin.top" :y2="margin.top + plotH"
          class="event-line"
        >
          <title>{{ ev.title }}</title>
        </line>

        <!-- Filled areas -->
        <path :d="scopeArea" class="scope-area" fill="url(#burnup-scope-fill)" />
        <path :d="delivArea" class="deliv-area" fill="url(#burnup-deliv-fill)" />

        <!-- Lines (animated via stroke-dasharray) -->
        <path :d="idealPath" class="line line-ideal" />
        <path :d="scopePath" class="line line-scope" />
        <path :d="delivPath" class="line line-deliv" />

        <!-- Dot markers -->
        <circle
          v-for="(p, i) in series"
          :key="`s${i}`"
          :cx="xForIdx(i)" :cy="yForVal(p.scope_sp)"
          r="3" class="dot dot-scope"
        />
        <circle
          v-for="(p, i) in series"
          v-show="p.delivered_sp > 0"
          :key="`d${i}`"
          :cx="xForIdx(i)" :cy="yForVal(p.delivered_sp)"
          r="3.5" class="dot dot-deliv"
        />

        <!-- Date labels -->
        <text :x="margin.left" :y="H - 12" class="tick" text-anchor="start">{{ firstDate }}</text>
        <text :x="margin.left + plotW" :y="H - 12" class="tick" text-anchor="end">{{ lastDate }}</text>
      </svg>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import type { BurnupPoint, ScopeEvent } from "@/api/types";

const props = defineProps<{
  series?: BurnupPoint[];
  events?: ScopeEvent[];
}>();

const series = computed<BurnupPoint[]>(() => props.series ?? []);
const events = computed<ScopeEvent[]>(() => props.events ?? []);
const hasData = computed(() => series.value.length > 0);

// Geometry — viewBox is logical; the SVG scales to its host width.
const W = 800;
const H = 280;
const margin = { top: 20, right: 20, bottom: 36, left: 40 };
const plotW = W - margin.left - margin.right;
const plotH = H - margin.top - margin.bottom;

const ymax = computed(() => {
  if (series.value.length === 0) return 0;
  const peak = Math.max(1, ...series.value.map((p) => p.scope_sp));
  // Round up to a clean tick so the gridline math reads nicely.
  return Math.ceil(peak / 5) * 5 + 5;
});

// Four evenly-spaced ticks: 0, ~third, ~two-thirds, max.
const yTicks = computed(() => {
  const m = ymax.value;
  if (m === 0) return [0];
  return [0, Math.round(m / 3), Math.round((m * 2) / 3), m];
});

function xForIdx(i: number): number {
  const n = series.value.length;
  if (n <= 1) return margin.left;
  return margin.left + (i / (n - 1)) * plotW;
}
function yForVal(v: number): number {
  if (ymax.value === 0) return margin.top + plotH;
  return margin.top + (1 - v / ymax.value) * plotH;
}

function buildPath(values: number[]): string {
  return values.map((v, i) => `${i ? "L" : "M"}${xForIdx(i)},${yForVal(v)}`).join(" ");
}
function buildArea(path: string, n: number): string {
  if (n === 0) return "";
  return `${path} L${xForIdx(n - 1)},${yForVal(0)} L${xForIdx(0)},${yForVal(0)} Z`;
}

const scopePath = computed(() => buildPath(series.value.map((p) => p.scope_sp)));
const delivPath = computed(() => buildPath(series.value.map((p) => p.delivered_sp)));
const scopeArea = computed(() => buildArea(scopePath.value, series.value.length));
const delivArea = computed(() => buildArea(delivPath.value, series.value.length));

// Ideal: linear interpolation from 0 → final scope.
const idealPath = computed(() => {
  const n = series.value.length;
  if (n === 0) return "";
  const final = series.value[n - 1].scope_sp;
  return Array.from({ length: n }, (_, i) =>
    `${i ? "L" : "M"}${xForIdx(i)},${yForVal(((i + 1) / n) * final)}`,
  ).join(" ");
});

const firstDate = computed(() => series.value[0]?.date ?? "");
const lastDate = computed(() => series.value[series.value.length - 1]?.date ?? "");

interface EventMark { date: string; x: number; title: string }
const eventLines = computed<EventMark[]>(() => {
  const dates = series.value.map((p) => p.date);
  return events.value
    .map((ev) => {
      const idx = dates.indexOf(ev.date);
      if (idx < 0) return null;
      return {
        date: ev.date,
        x: xForIdx(idx),
        title: `+${ev.sp_added} SP added on ${ev.date} (${ev.count} ticket(s)${ev.sample_keys.length ? `: ${ev.sample_keys.join(", ")}` : ""})`,
      };
    })
    .filter((e): e is EventMark => e !== null);
});

// === Reveal animation: stroke-dasharray draw + fill fade-in ===
const host = ref<HTMLElement | null>(null);
let played = false;

function playReveal(): void {
  const root = host.value;
  if (!root) return;
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  const env = (import.meta as ImportMeta).env as { MODE?: string; VITEST?: boolean } | undefined;
  const isTest = env?.MODE === "test" || env?.VITEST === true;

  const lines = root.querySelectorAll<SVGPathElement>(".line");
  const scopeArea = root.querySelector<SVGPathElement>(".scope-area");
  const delivArea = root.querySelector<SVGPathElement>(".deliv-area");
  const dots = root.querySelectorAll<SVGCircleElement>(".dot");

  if (reduced || isTest) {
    lines.forEach((p) => { p.style.strokeDasharray = ""; p.style.strokeDashoffset = "0"; });
    if (scopeArea) scopeArea.style.opacity = "1";
    if (delivArea) delivArea.style.opacity = "1";
    dots.forEach((d) => { d.style.opacity = "1"; });
    return;
  }

  // Reset to hidden state, then animate in.
  lines.forEach((p) => {
    const len = p.getTotalLength();
    p.style.strokeDasharray = `${len}`;
    p.style.strokeDashoffset = `${len}`;
    p.style.transition = "stroke-dashoffset 1.4s cubic-bezier(.5,.05,.2,1)";
  });
  if (scopeArea) {
    scopeArea.style.opacity = "0";
    scopeArea.style.transition = "opacity 0.8s ease 0.8s";
  }
  if (delivArea) {
    delivArea.style.opacity = "0";
    delivArea.style.transition = "opacity 0.8s ease 1.2s";
  }
  dots.forEach((d, i) => {
    d.style.opacity = "0";
    const cls = d.getAttribute("class") || "";
    const baseDelay = cls.includes("dot-deliv") ? 1.6 : 1.2;
    d.style.transition = `opacity 0.3s ease ${baseDelay + i * 0.025}s`;
  });

  requestAnimationFrame(() => {
    lines.forEach((p) => { p.style.strokeDashoffset = "0"; });
    if (scopeArea) scopeArea.style.opacity = "1";
    if (delivArea) delivArea.style.opacity = "1";
    dots.forEach((d) => { d.style.opacity = "1"; });
  });
}

onMounted(async () => {
  await nextTick();
  if (hasData.value && !played) {
    played = true;
    playReveal();
  }
});

watch(
  () => series.value.length,
  async (n) => {
    if (n > 0 && !played) {
      played = true;
      await nextTick();
      playReveal();
    }
  },
);
</script>

<style scoped>
.burnup {
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 24px;
  display: grid;
  gap: 18px;
}

.burnup-head {
  display: flex; flex-wrap: wrap;
  align-items: baseline; justify-content: space-between;
  gap: 12px;
}
.burnup-title {
  font-size: 18px; font-weight: 500;
  letter-spacing: -0.015em;
  margin: 0 0 2px;
  color: var(--ink);
}
.burnup-sub { font-size: 13px; color: var(--muted); }

.legend {
  display: flex; gap: 16px;
  font-size: 12px; color: var(--muted);
  flex-wrap: wrap;
}
.leg { display: inline-flex; align-items: center; gap: 6px; }
.dot {
  width: 10px; height: 10px;
  border-radius: 3px;
  display: inline-block;
}
.dot.scope {
  background: color-mix(in srgb, var(--blue) 25%, white);
  border: 1px solid var(--blue);
}
.dot.delivered { background: var(--green); }
.dot.ideal {
  background: transparent;
  border: 1px dashed var(--muted-2);
}
.dot.event { background: var(--amber); }

.burnup-host { width: 100%; }
.burnup-svg { width: 100%; height: 280px; display: block; }

.grid-line { stroke: var(--line-2); stroke-width: 1; }
.tick {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px;
  fill: var(--muted-2);
}

.event-line {
  stroke: var(--amber);
  stroke-width: 1;
  stroke-dasharray: 3 3;
}

.line { fill: none; }
.line-ideal {
  stroke: var(--muted-2);
  stroke-width: 1.2;
  stroke-dasharray: 4 4;
}
.line-scope {
  stroke: var(--blue);
  stroke-width: 2;
}
.line-deliv {
  stroke: var(--green);
  stroke-width: 2.5;
}

.dot-scope {
  fill: white;
  stroke: var(--blue);
  stroke-width: 1.5;
}
.dot-deliv {
  fill: var(--green);
}
</style>
